import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Box, Typography, Card, CardContent, Button, Divider, CircularProgress, Alert } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import apiClient from '../../config/axios';
import { paypalPaymentService } from '../membership-workflow/paypalPaymentService';
import getConfig from '../../config/app-config';

const CourseUpkeepCheckout = () => {
  const location = useLocation();
  const enrollment = location.state?.enrollment;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get configuration
  const config = getConfig();

  if (!enrollment) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error">Dati iscrizione non trovati.</Typography>
        </Box>
      </Container>
    );
  }

  const isEnrollmentPaid = enrollment.paymentStatusInfo?.isEnrollmentPaid;
  const rateAmount = enrollment.amount;

  const handlePayStripe = async () => {
    setLoading(true);
    setError(null);
    try {
      // Chiamata API per creare sessione Stripe upkeep
      const res = await apiClient.post('/api/enrollments/create-payment-session-upkeep-course', {
        enrollmentId: enrollment._id
      });
      const { checkoutUrl } = res.data;
      window.location.href = checkoutUrl;
    } catch (err) {
      setError('Errore durante la creazione della sessione di pagamento.');
      setLoading(false);
    }
  };

  const handlePayPalRedirect = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Prepare course data for PayPal order
      const courseData = {
        courseId: enrollment.course._id, // non serve, serve solo l'enrollmentId
        enrollmentId: enrollment._id,
        name: enrollment.course.name || 'Pagamento Rata Corso',
        amount: rateAmount,
        currency: 'EUR'
      };
      
      const result = await paypalPaymentService.createPayPalUpkeepOrder(courseData);
      
      if (result.success) {
        // Redirect to PayPal
        await paypalPaymentService.redirectToPayPal(result.approvalUrl);
      } else {
        console.error('PayPal upkeep order creation failed:', result.error);
        setError(`Errore nella creazione dell'ordine PayPal: ${result.error}`);
      }
    } catch (error) {
      console.error('Error in handlePayPalRedirect:', error);
      setError(`Errore nella creazione dell'ordine PayPal: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: 4 }}>
        <Card sx={{ boxShadow: 2, borderRadius: 3, background: 'white', border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h5" fontWeight={700} color="primary.main" gutterBottom>
              Pagamento Rata Corso
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'primary.main', fontSize: 20, letterSpacing: 0.2, mb: 1 }}>
              {enrollment.course?.name}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                mb: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                  <b>Docente:</b> {enrollment.course?.teacher ? `${enrollment.course.teacher.name} ${enrollment.course.teacher.surname}` : 'Da assegnare'}
                </Typography>
                {enrollment.course?.startDate && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                    <b>Data Inizio Corso:</b> {dayjs(enrollment.course.startDate).locale('it').format('DD-MMM-YY')}
                  </Typography>
                )}
                {enrollment.course?.endDate && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                    <b>Data Fine Corso:</b> {dayjs(enrollment.course.endDate).locale('it').format('DD-MMM-YY')}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                  <b>Cadenza di Pagamento:</b> {enrollment.paymentMethod}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                  <b>Importo rata:</b> €{rateAmount}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                  <b>Prossimo Pagamento:</b> {enrollment.paymentStatusInfo?.nextPaymentDate ? dayjs(enrollment.paymentStatusInfo.nextPaymentDate).locale('it').format('DD-MMM-YY') : '--'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
                  <b>Pagamenti:</b> {enrollment.paymentStatusInfo?.paymentsMade ?? '--'}/{enrollment.totalPayments ?? '--'}
                </Typography>
                <Typography variant="body2" color={enrollment.paymentStatusInfo?.paymentsLate > 0 ? 'error' : 'text.secondary'} sx={{ fontSize: 14 }}>
                  <b>Pagamenti in Ritardo:</b> {enrollment.paymentStatusInfo?.paymentsLate ?? 0}
                </Typography>
              </Box>
            </Box>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Button variant="contained" color="primary" size="large" onClick={handlePayStripe} disabled={isEnrollmentPaid || loading} sx={{ fontWeight: 700, borderRadius: 2, minWidth: 0, px: 3, py: 1, fontSize: 16, boxShadow: 0 }}>
                Paga con Carta
              </Button>
              
              {/* Mostra PayPal solo se abilitato nella configurazione */}
              {config.USE_PAYPAL && (
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="large" 
                  onClick={handlePayPalRedirect} 
                  disabled={isEnrollmentPaid || loading} 
                  sx={{ 
                    fontWeight: 700, 
                    borderRadius: 2, 
                    minWidth: 0, 
                    px: 3, 
                    py: 1, 
                    fontSize: 16, 
                    boxShadow: 0 
                  }}
                >
                  Paga con PayPal
                </Button>
              )}
            </Box>
            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><CircularProgress size={28} /></Box>}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default CourseUpkeepCheckout; 